import { useGameStore } from '../store/gameStore';
import { useEffect, useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import { REALMS, roundClockSeconds, streakBonusPct } from '@wot/shared';
import { RealmIcon } from '../components/RealmIcon';
import { getGuestName } from '../lib/guestName';
import { sfxCorrect, sfxWrong, sfxTick, sfxStreak, sfxTimeUp, haptic } from '../lib/sfx';

export function Game() {
  const phase = useGameStore((s) => s.phase);
  const currentQuestion = useGameStore((s) => s.currentQuestion);
  const selectedAnswer = useGameStore((s) => s.selectedAnswer);
  const answerLocked = useGameStore((s) => s.answerLocked);
  const opponentAnswered = useGameStore((s) => s.opponentAnswered);
  const opponentLeft = useGameStore((s) => s.opponentLeft);
  const opponentNickname = useGameStore((s) => s.opponentNickname);
  const opponentScore = useGameStore((s) => s.opponentScore);
  const opponentLastCorrect = useGameStore((s) => s.opponentLastCorrect);
  const roundResult = useGameStore((s) => s.roundResult);
  const currentRound = useGameStore((s) => s.currentRound);
  const totalRounds = useGameStore((s) => s.totalRounds);
  const matchMode = useGameStore((s) => s.matchMode);
  const livesLeft = useGameStore((s) => s.livesLeft);
  const boardMyScore = useGameStore((s) => s.boardMyScore);
  const boardOppScore = useGameStore((s) => s.boardOppScore);
  const scores = useGameStore((s) => s.scores);
  const nickname = useGameStore((s) => s.nickname);
  const submitAnswer = useGameStore((s) => s.submitAnswer);
  const countdownValue = useGameStore((s) => s.countdownValue);
  const timerEndsAt = useGameStore((s) => s.timerEndsAt);
  const playerId = useGameStore((s) => s.playerId);
  const gameMode = useGameStore((s) => s.gameMode);

  const [timeRemaining, setTimeRemaining] = useState(15);
  const [flashClass, setFlashClass] = useState('');
  const [streak, setStreak] = useState(0);
  const [streakPop, setStreakPop] = useState(false);
  const [confetti, setConfetti] = useState<{ id: number; left: number; color: string; delay: number }[]>([]);
  const prevScoreRef = useRef(0);

  // ── Survival stakes, announced ────────────────────────────────────
  // The dots in the scoreboard state the count; this banner marks the
  // moments. Once at the start so the player knows the rules of the run,
  // and once each time a life goes, because losing one silently is how a
  // player discovers the mechanic at the results screen.
  const [lifeBanner, setLifeBanner] = useState<string | null>(null);
  const prevLivesRef = useRef<number | null>(null);
  useEffect(() => {
    if (matchMode !== 'SURVIVAL' || livesLeft === null) {
      prevLivesRef.current = livesLeft;
      return;
    }
    const prev = prevLivesRef.current;
    prevLivesRef.current = livesLeft;
    let text: string | null = null;
    // No intro blurb: the hearts in the bar already say the stakes. The
    // banner slot only speaks when something HAPPENS.
    if (prev !== null && livesLeft < prev) {
      text = livesLeft === 0 ? 'OUT OF LIVES'
        : livesLeft === 1 ? 'LAST LIFE'
        : `LIFE LOST. ${livesLeft} REMAIN`;
    }
    if (text) {
      setLifeBanner(text);
      const t = setTimeout(() => setLifeBanner(null), 2400);
      return () => clearTimeout(t);
    }
  }, [livesLeft, matchMode]);

  // The ring's denominator is this round's REAL clock. With a hardcoded 15
  // the ring sat pinned at 100% for the first seconds of a long round.
  const totalTime = roundClockSeconds(matchMode, currentQuestion?.difficulty as 'easy' | 'medium' | 'hard' | undefined, currentRound);
  const timerPercent = Math.min(100, (timeRemaining / totalTime) * 100);
  const isSolo = gameMode === 'solo';

  // Leaving. Solo and Daily walk out at once; a 1v1 asks first, because the
  // clock keeps running on the server and the walk-out is a forfeit. The
  // Android back button takes the same road (App.tsx fires 'wot-back').
  const [leaveAsk, setLeaveAsk] = useState(false);
  const requestLeave = () => {
    if (isSolo) { useGameStore.getState().resetGame(); return; }
    setLeaveAsk(true);
  };
  useEffect(() => {
    const onBack = () => requestLeave();
    window.addEventListener('wot-back', onBack);
    return () => window.removeEventListener('wot-back', onBack);
  }, [isSolo]);
  const myScore = playerId ? (scores[playerId] ?? 0) : 0;
  // What the scoreboard SHOWS: last-review totals while a round is live, the
  // real totals from the review phase on. A bar that jumps mid-round tells
  // you an answer was right before the reveal, and in multiplayer it tells
  // you about the opponent's answer too.
  const revealed = phase !== 'round_active';
  const shownMyScore = revealed ? myScore : boardMyScore;
  const shownOppScore = revealed ? opponentScore : boardOppScore;
  const letters = ['A', 'B', 'C', 'D'];
  const isCritical = timeRemaining <= 5 && timeRemaining > 0 && phase === 'round_active';

  // Realm color infection (design vision): the active realm's color drives
  // the timer gradient, realm tag and selected-answer accent for this round.
  const realmId = currentQuestion?.realm?.toUpperCase() || '';
  const realmColor = (REALMS as any)[realmId]?.color || '#3E7BFA';

  // ── Solo pause ──
  // A phone call or a trip to the home screen must not eat the round. While
  // the app is hidden the clock freezes: on return, timerEndsAt and the
  // reaction anchor shift forward by exactly the hidden time. Solo only;
  // a multiplayer opponent's clock cannot be paused.
  const hiddenAtRef = useRef<number | null>(null);
  useEffect(() => {
    if (gameMode !== 'solo') return;
    const onVisibility = () => {
      if (document.hidden) {
        hiddenAtRef.current = Date.now();
        return;
      }
      const hiddenAt = hiddenAtRef.current;
      hiddenAtRef.current = null;
      if (hiddenAt === null) return;
      const st = useGameStore.getState();
      if (st.phase !== 'round_active' || st.answerLocked) return;
      const pausedMs = Date.now() - hiddenAt;
      useGameStore.setState({
        timerEndsAt: (st.timerEndsAt ?? Date.now()) + pausedMs,
        roundStartedAt: (st.roundStartedAt ?? Date.now()) + pausedMs,
      });
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, [gameMode]);

  // ── Timer countdown ──
  useEffect(() => {
    if (!timerEndsAt || phase !== 'round_active') return;
    const interval = setInterval(() => {
      const remaining = Math.max(0, (timerEndsAt - Date.now()) / 1000);
      setTimeRemaining(remaining);

      // Tick sound for last 5 seconds (urgency increases as time runs out)
      if (remaining <= 5 && remaining > 0 && Math.floor(remaining) !== Math.floor(remaining + 0.1)) {
        sfxTick(5 - Math.floor(remaining));
      }

      if (remaining <= 0) {
        // Hidden solo app: do not burn the round; the pause handler will
        // shift the clock the moment the app comes back.
        if (document.hidden && useGameStore.getState().gameMode === 'solo') return;
        clearInterval(interval);
        const currentState = useGameStore.getState();
        if (!currentState.answerLocked && currentState.phase === 'round_active') {
          sfxTimeUp();
          haptic('error');
          currentState.submitAnswer(-1);
        }
      }
    }, 100);
    return () => clearInterval(interval);
  }, [timerEndsAt, phase]);

  // The opponent's clock ran out too. In a 1v1 the review waits for the
  // opponent's answer broadcast; an opponent who left the match never sends
  // one, and the round used to hang for ever at a dead clock. Four seconds
  // past the deadline the round is treated as timed out on their side.
  useEffect(() => {
    if (isSolo || !timerEndsAt || phase !== 'round_active') return;
    const id = setTimeout(() => {
      const s = useGameStore.getState();
      if (s.phase === 'round_active' && s.answerLocked && !s.opponentAnswered) {
        useGameStore.setState({ opponentAnswered: true });
        s.checkAndTriggerReview();
      }
    }, Math.max(0, timerEndsAt - Date.now()) + 4000);
    return () => clearTimeout(id);
  }, [timerEndsAt, phase, isSolo]);

  // ── Viewport flash + SFX on round result ──
  useEffect(() => {
    if (phase !== 'round_review' || !roundResult || !playerId) return;

    const myResult = roundResult.players[playerId];
    if (!myResult) return;

    if (myResult.pointsEarned > 0) {
      // Correct!
      setFlashClass('flash-correct');
      sfxCorrect();
      haptic('medium');
      // The server's count when it sends one: it is the run the bonus was
      // paid on. An older server sends none and the local count stands in.
      const serverStreak = typeof myResult.streak === 'number' ? myResult.streak : null;
      setStreak(s => {
        const next = serverStreak ?? s + 1;
        if (next >= 3) {
          sfxStreak();
          setStreakPop(true);
          setTimeout(() => setStreakPop(false), 400);
        }
        return next;
      });
    } else if (selectedAnswer !== -1) {
      // Wrong
      setFlashClass('flash-wrong');
      sfxWrong();
      haptic('error');
      setStreak(0);
    } else {
      // Timeout
      setStreak(0);
    }

    const timer = setTimeout(() => setFlashClass(''), 600);
    return () => clearTimeout(timer);
  }, [phase, roundResult, playerId, selectedAnswer]);

  // ── Confetti on perfect game (checked on results) ──
  useEffect(() => {
    if (phase === 'round_review' && roundResult && playerId) {
      // Check if this is the last round and all correct
      const myResult = roundResult.players[playerId];
      if (currentRound === totalRounds && myResult?.pointsEarned > 0 && streak + 1 === totalRounds) {
        // Perfect game! Confetti time!
        const pieces = Array.from({ length: 30 }, (_, i) => ({
          id: i,
          left: Math.random() * 100,
          color: ['#3E7BFA', '#14B8A6', '#F59E0B', '#F43F5E', '#fff'][Math.floor(Math.random() * 5)],
          delay: Math.random() * 0.8,
        }));
        setConfetti(pieces);
        setTimeout(() => setConfetti([]), 3000);
      }
    }
  }, [phase, roundResult, playerId, currentRound, totalRounds, streak]);

  // ── Reset streak on new game ──
  useEffect(() => {
    if (currentRound === 1 && phase === 'round_active') {
      setStreak(0);
    }
  }, [currentRound, phase]);

  // Countdown overlay
  if (phase === 'countdown' && countdownValue !== null) {
    return (
      <div className="countdown-overlay">
        <span className="countdown-number" key={countdownValue}>{countdownValue}</span>
      </div>
    );
  }

  return (
    <div className={`page-game ${flashClass}`} style={{ '--realm-accent': realmColor } as React.CSSProperties}>
      {/* The jersey number: the realm's mark at poster scale, ghosted behind
          the round. One oversized element per screen is what separates a
          composition from a stack of boxes. */}
      {realmId && (
        <div aria-hidden style={{
          position: 'absolute', top: 64, right: -90, zIndex: 0,
          opacity: 0.07, transform: 'rotate(-10deg)', pointerEvents: 'none',
        }}>
          <RealmIcon id={realmId} size={330} color={realmColor} />
        </div>
      )}

      {/* ── Confetti layer ── */}
      {confetti.map(p => (
        <div
          key={p.id}
          className="confetti-piece"
          style={{
            left: `${p.left}%`,
            backgroundColor: p.color,
            animationDelay: `${p.delay}s`,
          }}
        />
      ))}

      {/* Timer bar */}
      <div className="timer-bar">
        <div
          className={`timer-bar-fill ${isCritical ? 'timer-bar-critical' : ''}`}
          style={{ width: `${timerPercent}%` }}
        />
      </div>

      {/* Scoreboard */}
      <div className="scoreboard">
        {/* Each player is a column: name over score. Two names, two scores
            and the round pill never fit one row on a phone at a large font
            scale; the old row crushed the names to "K..." and clipped the
            opponent status dot into a stray mark. Columns get equal width,
            names ellipsize, scores and the pill never shrink. */}
        <div className="scoreboard-player">
          <span className="scoreboard-name">{nickname || getGuestName()}</span>
          <span className="scoreboard-score">{shownMyScore.toLocaleString('en-US')}</span>
        </div>

        {!isSolo ? (
          <div className="scoreboard-player scoreboard-opponent">
            <span className="scoreboard-name-row">
              {/* live status dot: green once they lock this round. Outside
                  the ellipsized text so it can never be clipped. */}
              <span style={{
                width: 6, height: 6, borderRadius: '50%', flexShrink: 0,
                background: opponentLeft ? '#F43F5E' : opponentAnswered ? '#14B8A6' : '#3E4E70',
                transition: 'background 0.2s',
              }} />
              <span className="scoreboard-name" style={{ color: '#ccccd4' }}>
                {(opponentNickname || 'WAITING').toUpperCase()}
              </span>
            </span>
            <span className="scoreboard-score" style={{ color: '#ccccd4' }}>
              {shownOppScore.toLocaleString('en-US')}
            </span>
          </div>
        ) : null}

        {/* Streak counter, and from three in a row the STREAK BONUS the run
            is paying (scored by submit-answer, every mode). */}
        {streak >= 2 && (
          <span className={`streak-badge ${streakPop ? 'streak-pop' : ''}`}>
            {streak}x
            {streakBonusPct(streak) > 0 && <span className="streak-badge-bonus">+{streakBonusPct(streak)}%</span>}
          </span>
        )}

        {/* Survival lives: the run's stakes must be readable at a glance from
            arm's length, not decoded from dots. Marks are large, labelled,
            and hollow out as they go. */}
        {matchMode === 'SURVIVAL' && livesLeft !== null && (
          <span style={{ display: 'inline-flex', gap: 5, alignItems: 'center', margin: '0 8px' }}
                aria-label={`${livesLeft} lives left`}>
            {[0, 1, 2].map(i => (
              <svg key={i} viewBox="0 0 24 24" width="19" height="19"
                style={{ filter: i < livesLeft ? 'drop-shadow(0 0 5px rgba(244,63,94,0.5))' : 'none', transition: 'all 0.25s ease' }}>
                <path
                  d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z"
                  fill={i < livesLeft ? '#F43F5E' : 'none'}
                  stroke={i < livesLeft ? 'none' : 'rgba(255,255,255,0.28)'}
                  strokeWidth="2"
                />
              </svg>
            ))}
          </span>
        )}

        <span className="scoreboard-round">
          {matchMode === 'SURVIVAL'
            ? String(currentRound).padStart(2, '0')
            : `${String(currentRound).padStart(2, '0')}/${String(totalRounds).padStart(2, '0')}`}
        </span>
        <button className="scoreboard-leave" onClick={requestLeave} aria-label="Leave the match" title="Leave">
          <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor" aria-hidden="true">
            <path d="M18.3 5.71a1 1 0 0 0-1.41 0L12 10.59 7.11 5.7A1 1 0 0 0 5.7 7.11L10.59 12 5.7 16.89a1 1 0 1 0 1.41 1.41L12 13.41l4.89 4.89a1 1 0 0 0 1.41-1.41L13.41 12l4.89-4.89a1 1 0 0 0 0-1.4z"/>
          </svg>
        </button>
      </div>

      {leaveAsk && createPortal(
        <div className="leave-confirm" role="dialog" aria-label="Leave the match?" onClick={() => setLeaveAsk(false)}>
          <div className="leave-confirm-box" onClick={(e) => e.stopPropagation()}>
            <div className="leave-confirm-title">LEAVE THE MATCH?</div>
            <div className="leave-confirm-text">The clock keeps running. Leaving now counts as a loss.</div>
            <div className="leave-confirm-actions">
              <button className="btn btn-ghost" onClick={() => setLeaveAsk(false)}>STAY</button>
              <button className="btn btn-danger" onClick={() => { void useGameStore.getState().leaveMatch(); }}>LEAVE</button>
            </div>
          </div>
        </div>,
        document.body,
      )}


      {/* ── Survival life strip ──
          In the layout flow, never floating: an overlay landed on top of the
          question. The strip reserves its height for the whole run, so the
          banner appearing and fading moves nothing around it. */}
      {matchMode === 'SURVIVAL' && (
        <div style={{
          height: 34, display: 'flex', alignItems: 'center', justifyContent: 'center',
          flexShrink: 0,
        }}>
          {lifeBanner && (
            <span style={{
              whiteSpace: 'nowrap',
              background: 'rgba(244, 63, 94, 0.08)', border: '1px solid rgba(244, 63, 94, 0.55)',
              borderRadius: 999, padding: '5px 16px',
              fontFamily: 'var(--font-mono)', fontWeight: 700,
              fontSize: '0.8rem', letterSpacing: '0.18em', color: '#fda4af',
            }}>
              {lifeBanner}
            </span>
          )}
        </div>
      )}

      {/* Question area */}
      {currentQuestion && phase === 'round_active' && (
        <div className="animate-in game-content-wrapper">

          {/* ── HUD: realm chip | countdown ring | difficulty chip ──
              The clock was a 6px bar at the very top of the screen, which
              nobody reads mid-question. The ring puts the remaining seconds
              where the eyes already are, in the realm's color, and goes
              coral for the last five. */}
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            gap: 10, marginBottom: 6,
          }}>
            {/* The chip gives way, never the clock or the difficulty: at a
                large system font the realm name ellipsizes instead of pushing
                the badge off the right edge of the screen. */}
            <span className="realm-tag hud-chip" style={{
              marginBottom: 0, display: 'inline-flex', alignItems: 'center', gap: 7,
              background: `${realmColor}1C`, border: `1px solid ${realmColor}55`,
              borderRadius: 999,
              width: 'auto', flex: '1 1 auto', minWidth: 0, overflow: 'hidden',
            }}>
              <RealmIcon id={realmId} size={16} />
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 }}>
                {currentQuestion.realm?.toUpperCase() || 'TRIVIA'}
              </span>
            </span>
            {(() => {
              const secs = Math.max(0, Math.ceil(timeRemaining));
              const frac = Math.max(0, Math.min(1, totalTime > 0 ? timeRemaining / totalTime : 0));
              const R = 25, C = 2 * Math.PI * R;
              const hue = isCritical ? '#F43F5E' : realmColor;
              return (
                <span style={{ position: 'relative', width: 62, height: 62, flexShrink: 0 }}>
                  <svg width="62" height="62" viewBox="0 0 62 62" style={{ transform: 'rotate(-90deg)' }}>
                    <circle cx="31" cy="31" r={R} fill="#16223A" stroke="rgba(255,255,255,0.12)" strokeWidth="5" />
                    <circle cx="31" cy="31" r={R} fill="none" stroke={hue} strokeWidth="5"
                      strokeLinecap="round" strokeDasharray={C} strokeDashoffset={C * (1 - frac)}
                      style={{ transition: 'stroke-dashoffset 0.2s linear, stroke 0.3s' }} />
                  </svg>
                  <span style={{
                    position: 'absolute', inset: 0, display: 'flex', alignItems: 'center',
                    justifyContent: 'center', fontFamily: 'var(--font-mono)', fontWeight: 800,
                    fontSize: '1.35rem', color: isCritical ? '#FB7185' : '#fff',
                  }}>
                    {secs}
                  </span>
                </span>
              );
            })()}
            {currentQuestion.difficulty ? (
              <span className="hud-diff" style={{
                borderRadius: 999,
                fontWeight: 700,
                border: '1px solid',
                flexShrink: 0, whiteSpace: 'nowrap',
                color: currentQuestion.difficulty === 'easy' ? '#34D399' : currentQuestion.difficulty === 'medium' ? '#FBBF24' : '#F87171',
                borderColor: currentQuestion.difficulty === 'easy' ? '#10B98155' : currentQuestion.difficulty === 'medium' ? '#F59E0B55' : '#EF444455',
                backgroundColor: currentQuestion.difficulty === 'easy' ? '#10B9811A' : currentQuestion.difficulty === 'medium' ? '#F59E0B1A' : '#EF44441A',
              }}>
                {currentQuestion.difficulty.toUpperCase()}
              </span>
            ) : <span style={{ width: 62 }} />}
          </div>

          {/* Question */}
          <p className="question-text">{currentQuestion.question}</p>

          {/* Opponent indicator */}
          {!isSolo && opponentLeft && (
            <div className="opponent-indicator opponent-indicator-left" style={{ padding: '8px 24px' }}>
              <span className="opponent-dot" style={{ background: '#F43F5E' }} />
              <span>opponent left the match</span>
            </div>
          )}
          {!isSolo && !opponentLeft && opponentAnswered && (
            <div className="opponent-indicator" style={{ padding: '8px 24px' }}>
              <span className="opponent-dot" />
              <span>opponent answered</span>
            </div>
          )}

          {/* 2×2 answer grid */}
          <div className="answer-grid">
            {currentQuestion.choices.map((choice, i) => (
              <button
                key={i}
                className={`answer-card ${selectedAnswer === i ? 'answer-selected' : ''} ${answerLocked && selectedAnswer !== i ? 'answer-locked' : ''}`}
                onClick={() => { haptic('medium'); submitAnswer(i); }}
                disabled={answerLocked}
                id={`answer-${letters[i]}`}
              >
                <span className="letter-badge">{letters[i]}</span>
                <span>{choice}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Round Review */}
      {phase === 'round_review' && roundResult && currentQuestion && (
        <div className="animate-in game-content-wrapper">
          <div style={{ display: 'flex', gap: '12px', alignItems: 'center', marginBottom: '8px' }}>
            <span className="realm-tag" style={{ marginBottom: 0, display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              <RealmIcon id={realmId} size={15} />
              {currentQuestion.realm?.toUpperCase() || 'TRIVIA'}
            </span>
            {currentQuestion.difficulty && (
              <span style={{
                fontSize: '0.8rem',
                letterSpacing: '0.15em',
                padding: '4px 8px',
                borderRadius: '4px',
                fontWeight: 600,
                border: '1px solid',
                color: currentQuestion.difficulty === 'easy' ? '#10B981' : currentQuestion.difficulty === 'medium' ? '#F59E0B' : '#EF4444',
                borderColor: currentQuestion.difficulty === 'easy' ? '#10B98155' : currentQuestion.difficulty === 'medium' ? '#F59E0B55' : '#EF444455',
                backgroundColor: currentQuestion.difficulty === 'easy' ? '#10B98111' : currentQuestion.difficulty === 'medium' ? '#F59E0B11' : '#EF444411',
              }}>
                {currentQuestion.difficulty.toUpperCase()}
              </span>
            )}
          </div>
          <p className="question-text" style={{ color: '#ccccd4' }}>{currentQuestion.question}</p>

          {/* Timeout graphic */}
          {selectedAnswer === -1 && (
            <div className="timeout-text">TIME'S UP!</div>
          )}

          <div className="answer-grid">
            {currentQuestion.choices.map((choice, i) => {
              const isCorrect = i === roundResult.correctIndex;
              const isSelected = selectedAnswer === i;
              const isWrong = isSelected && !isCorrect;
              return (
                <div
                  key={i}
                  className={`answer-card ${isCorrect ? 'answer-correct answer-correct-anim' : ''} ${isWrong ? 'answer-wrong answer-wrong-anim' : ''} ${!isCorrect && !isWrong ? 'answer-locked' : ''}`}
                >
                  <span className="letter-badge">{letters[i]}</span>
                  <span>{choice}</span>
                  {isCorrect && <span style={{ marginLeft: 'auto', color: '#14B8A6' }}>✓</span>}
                  {isWrong && <span style={{ marginLeft: 'auto', color: '#F43F5E' }}>✗</span>}
                </div>
              );
            })}
          </div>

          {/* Opponent result — only in the review phase, where the answer is
              already revealed to both players (no information leak). */}
          {!isSolo && (
            <div style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              marginTop: 12, padding: '8px 12px', borderRadius: 8,
              background: '#16223A', border: '1px solid #33415E',
            }}>
              <span style={{
                fontFamily: 'var(--font-mono)', fontSize: '0.85rem',
                letterSpacing: '0.08em', color: '#ccccd4',
              }}>
                {(opponentNickname || 'OPPONENT').toUpperCase()}
              </span>
              <span style={{
                fontFamily: 'var(--font-heading)', fontWeight: 800,
                fontSize: '0.85rem', letterSpacing: '0.08em',
                color: opponentLastCorrect === true ? '#14B8A6'
                     : opponentLastCorrect === false ? '#F43F5E' : '#bebec6',
              }}>
                {opponentLastCorrect === true ? 'CORRECT'
                 : opponentLastCorrect === false ? 'WRONG' : 'NO ANSWER'}
              </span>
            </div>
          )}

          {/* Points animation */}
          {playerId && roundResult.players[playerId] && roundResult.players[playerId].pointsEarned > 0 && (
            <div className="huge-points-anim" style={{ color: '#14B8A6', textShadow: '0 0 40px rgba(20, 184, 166, 0.8)' }}>
              +{roundResult.players[playerId].pointsEarned}
              {(roundResult.players[playerId].streakBonus ?? 0) > 0 && (
                <div className="points-streak">
                  STREAK +{streakBonusPct(roundResult.players[playerId].streak ?? 0)}%
                </div>
              )}
            </div>
          )}
          {playerId && roundResult.players[playerId] && roundResult.players[playerId].pointsEarned === 0 && selectedAnswer !== -1 && (
            <div className="huge-points-anim" style={{ color: '#F43F5E', textShadow: '0 0 40px rgba(244, 63, 94, 0.8)' }}>
              +0
            </div>
          )}
        </div>
      )}

      {/* Empty state */}
      {!currentQuestion && phase === 'waiting' && (
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 16 }}>
          <span style={{ color: '#ccccd4', fontSize: '0.9rem' }}>Waiting for game to start...</span>
        </div>
      )}

      {/* Dynamic Style Block for JUICE */}
      <style>{`
        @keyframes shake {
          0%, 100% { transform: translateX(0); }
          20% { transform: translateX(-8px); }
          40% { transform: translateX(8px); }
          60% { transform: translateX(-8px); }
          80% { transform: translateX(8px); }
        }
        @keyframes pulse-correct {
          0% { transform: scale(1); box-shadow: 0 0 0px rgba(20, 184, 166, 0); }
          50% { transform: scale(1.02); box-shadow: 0 0 25px rgba(20, 184, 166, 0.4); }
          100% { transform: scale(1); box-shadow: 0 0 0px rgba(20, 184, 166, 0); }
        }
        @keyframes big-float-up {
          0% { transform: translate(-50%, 40px) scale(0.5); opacity: 0; }
          15% { transform: translate(-50%, 0px) scale(1.3); opacity: 1; }
          30% { transform: translate(-50%, -10px) scale(1.1); opacity: 1; }
          80% { transform: translate(-50%, -40px) scale(1); opacity: 1; }
          100% { transform: translate(-50%, -60px) scale(0.9); opacity: 0; }
        }
        @keyframes correct-flash {
          0% { box-shadow: inset 0 0 0 0 rgba(20, 184, 166, 0); }
          30% { box-shadow: inset 0 0 120px 20px rgba(20, 184, 166, 0.12); }
          100% { box-shadow: inset 0 0 0 0 rgba(20, 184, 166, 0); }
        }
        @keyframes wrong-flash {
          0% { box-shadow: inset 0 0 0 0 rgba(244, 63, 94, 0); }
          30% { box-shadow: inset 0 0 120px 20px rgba(244, 63, 94, 0.12); }
          100% { box-shadow: inset 0 0 0 0 rgba(244, 63, 94, 0); }
        }
        @keyframes streak-pop-anim {
          0% { transform: scale(1); }
          50% { transform: scale(1.5); }
          100% { transform: scale(1); }
        }
        @keyframes timer-pulse {
          0%, 100% { opacity: 0.4; transform: translate(-50%) scale(1); }
          50% { opacity: 0.8; transform: translate(-50%) scale(1.1); }
        }
        @keyframes confetti-fall {
          0% { transform: translateY(-20px) rotate(0deg); opacity: 1; }
          100% { transform: translateY(100vh) rotate(720deg); opacity: 0; }
        }

        .flash-correct { animation: correct-flash 0.6s ease-out; }
        .flash-wrong { animation: wrong-flash 0.5s ease-out; }

        .answer-wrong-anim {
          animation: shake 0.4s ease-in-out;
          background: linear-gradient(145deg, #320a18, #1a050c) !important;
          border-color: #F43F5E !important;
        }
        .answer-correct-anim {
          animation: pulse-correct 1.5s infinite ease-in-out;
          background: linear-gradient(145deg, #0f3c36, #061a17) !important;
          border-color: #14B8A6 !important;
          position: relative;
          z-index: 10;
        }
        .huge-points-anim {
          position: fixed;
          top: 40%;
          left: 50%;
          font-size: 6rem;
          font-weight: 900;
          font-family: var(--wot-font-display);
          text-align: center;
          pointer-events: none;
          z-index: 999;
          animation: big-float-up 2.5s cubic-bezier(0.175, 0.885, 0.32, 1.275) forwards;
        }
        .timeout-text {
          color: #F43F5E;
          font-size: 2.2rem;
          font-weight: 900;
          text-align: center;
          letter-spacing: 0.2em;
          text-shadow: 0 0 25px rgba(244, 63, 94, 0.8);
          margin-bottom: 24px;
        }
        .streak-badge {
          font-size: 0.85rem;
          font-weight: 800;
          color: #F59E0B;
          margin-left: auto;
          margin-right: 8px;
        }
        .streak-pop {
          animation: streak-pop-anim 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
        }
        .streak-badge-bonus {
          margin-left: 4px;
          font-family: var(--font-mono);
          font-size: 0.72rem;
          letter-spacing: 0.04em;
        }
        /* Under the round's points: how much of them the run paid. */
        .points-streak {
          margin-top: 2px;
          font-family: var(--font-mono);
          font-size: 1.05rem;
          font-weight: 800;
          letter-spacing: 0.18em;
          color: #F59E0B;
          text-shadow: 0 0 18px rgba(245, 158, 11, 0.45);
        }
        .timer-bar-critical .timer-bar-fill,
        .timer-bar-critical {
          background: #F43F5E !important;
        }
        /* Compact top-right badge (was a full-screen 12rem number centered
           over the answers: it covered the choices, spiked panic, and on
           older WebViews the oversized element pushed the layout / shrank
           the page each round). */
        .timer-seconds-critical {
          position: fixed;
          top: 54px;
          right: 18px;
          font-size: 2.6rem;
          font-weight: 900;
          line-height: 1;
          color: #F43F5E;
          text-shadow: 0 0 18px rgba(244, 63, 94, 0.55);
          pointer-events: none;
          z-index: 50;
          font-family: var(--font-mono);
          animation: badge-pulse 1s ease-in-out infinite;
        }
        @keyframes badge-pulse {
          0%, 100% { transform: scale(1); opacity: 0.9; }
          50% { transform: scale(1.12); opacity: 1; }
        }
        .confetti-piece {
          position: fixed;
          width: 8px;
          height: 8px;
          top: 0;
          border-radius: 2px;
          animation: confetti-fall 2.5s ease-in forwards;
          z-index: 9999;
          pointer-events: none;
        }
      `}</style>
    </div>
  );
}
